import { clsx, type ClassValue } from 'clsx';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import { twMerge } from 'tailwind-merge';

dayjs.extend(relativeTime);
dayjs.extend(utc);

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Shared thin scrollbar, matching the dashboard's sidebar treatment. */
export const SCROLLBAR_CLASSES = [
  '[&::-webkit-scrollbar]:w-2',
  '[&::-webkit-scrollbar-track]:bg-transparent',
  '[&::-webkit-scrollbar-thumb]:bg-sidebar-border',
  '[&::-webkit-scrollbar-thumb]:rounded-full',
  '[&::-webkit-scrollbar-thumb]:hover:bg-sidebar-border/80',
].join(' ');

/** "office_manager" → "Office Manager"; also tidies dotted placement ids. */
export function normalizeText(text: string | undefined | null): string {
  if (!text) return '—';
  return text
    .split(/[_.]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function formatDateTime(
  date: Date | string | undefined | null,
  format = 'MMM DD, YYYY, hh:mm A'
): string {
  if (!date || !dayjs(date).isValid()) return '—';
  return dayjs(date).format(format);
}

export function formatRelativeTime(date: Date | string | undefined | null): string {
  if (!date || !dayjs(date).isValid()) return '—';
  return dayjs(date).fromNow();
}

/**
 * Flight dates and rollup buckets are UTC calendar days, so they are formatted
 * in UTC too. Rendering `2026-08-05` in a browser west of Greenwich would
 * otherwise show August 4th, which makes a flight look a day short.
 */
export function formatUtcDate(date: string | undefined | null, format = 'MMM D, YYYY'): string {
  if (!date) return '—';
  const parsed = dayjs.utc(date);
  return parsed.isValid() ? parsed.format(format) : '—';
}

export const todayUtc = (): string => dayjs.utc().format('YYYY-MM-DD');

export const daysAgoUtc = (days: number): string =>
  dayjs.utc().subtract(days, 'day').format('YYYY-MM-DD');

/** Compact integers for stat tiles: 1234 → "1,234". */
export const formatCount = (value: number): string => value.toLocaleString('en-US');

/** Ratios arrive as 0–1 or null when the denominator was zero. */
export function formatPercent(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(digits)}%`;
}

export function getInitials(name: string | null | undefined, email: string | null | undefined) {
  if (name) {
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0]!.charAt(0)}${parts[parts.length - 1]!.charAt(0)}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }
  return email?.substring(0, 2).toUpperCase() ?? 'U';
}
